import Box from "@/ui/atoms/Box";
import React, { useCallback } from "react";
import { Icon, IconBox } from "@proda-ai/ui-kit";
import type { PropertyAddressProps } from "./BasePropertyAddressForm";
import BasePropertyAddressForm from "./BasePropertyAddressForm";
import type { PropertyDetailsProps } from "./BasePropertyDetailsForm";
import BasePropertyDetailsForm from "./BasePropertyDetailsForm";
import type {
    MultipropPropertyInfo,
    PropertyAddressCheck,
    PropertyAddressCheckInfo,
    MultipropPropertyFormShape,
    MultipropBasePropertyInfo,
    PropertyNameCheck,
    NewProperty,
} from "@/ui/organisms/dealTypes";
import { creationTestIds } from "./utils";
import FormSteps from "./FormSteps";

export type PropertyFormProps<T extends MultipropBasePropertyInfo> = {
    property: Partial<T>;
    databaseId: number;
    onSubmit: (data: T) => Promise<void>;
    onBack: () => void;
    setPropertyAddressCheck: (
        data: PropertyAddressCheckInfo,
    ) => Promise<PropertyAddressCheck>;
    setPropertyNameCheck: (
        databaseId: number,
        propertyName: string,
    ) => Promise<PropertyNameCheck>;
    propertyAddressCheck: PropertyAddressCheck | null;
    mutateNewProperty: (
        data: Partial<NewProperty | MultipropPropertyInfo>,
    ) => void;
    configGoogleMapsState: {
        status: "idle" | "pending" | "resolved" | "rejected";
        data: {
            apiKey: string;
        } | null;
    };
    getCurrentDate: () => Date;
};

const PropertyForm = <T extends MultipropBasePropertyInfo>(
    props: PropertyFormProps<T>,
) => {
    const { onBack, onSubmit } = props;
    const [propertyFormShape, changePropertyFormShape] = React.useState({
        isAddressFormCollapsed: false,
        isDetailsFormCollapsed: false,
    } as MultipropPropertyFormShape);

    const [isAddressFormDirty, setIsAddressFormDirty] = React.useState(false);

    const isValid =
        propertyFormShape.isAddressFormCollapsed &&
        propertyFormShape.isDetailsFormCollapsed;

    const { mutateNewProperty, property } = props;
    const handleResidentialFieldsClear = useCallback(
        () =>
            mutateNewProperty({
                residentialUnits: null,
                // "Other" mirrors Excelsior free text, which exists regardless
                // of asset type — preserve it so a save doesn't wipe the
                // original value (the backend never writes "other" back).
                subAssetType:
                    property?.subAssetType === "other" ? "other" : null,
                buildingType: null,
            }),
        [mutateNewProperty, property?.subAssetType],
    );

    const addressProps: PropertyAddressProps = {
        ...props,
        isCollapsed: propertyFormShape.isAddressFormCollapsed,
        setIsAddressFormDirty,
        onEdit: () =>
            changePropertyFormShape({
                ...propertyFormShape,
                isAddressFormCollapsed: false,
            }),
        onSave: (data: Partial<NewProperty | MultipropPropertyInfo>) => {
            void props.mutateNewProperty(data);
            changePropertyFormShape({
                ...propertyFormShape,
                isAddressFormCollapsed: true,
            });
        },
        setPropertyNameCheck: props.setPropertyNameCheck,
    };
    const detailsProps: PropertyDetailsProps = {
        ...props,
        isCollapsed: propertyFormShape.isDetailsFormCollapsed,
        onEdit: () =>
            changePropertyFormShape({
                ...propertyFormShape,
                isDetailsFormCollapsed: false,
            }),
        onSave: (data: Partial<NewProperty | MultipropPropertyInfo>) => {
            void props.mutateNewProperty(data);
            changePropertyFormShape({
                ...propertyFormShape,
                isDetailsFormCollapsed: true,
            });
        },
        onResidentialFieldsClear: handleResidentialFieldsClear,
    };
    return (
        <Box>
            <BasePropertyAddressForm {...addressProps}>
                {({ propertyAddressCheck }) => {
                    return (
                        <Box>
                            {propertyAddressCheck !== null &&
                            propertyAddressCheck.hasExactMatch === true &&
                            isAddressFormDirty ? (
                                <span
                                    style={{
                                        fontSize: "0.8rem",
                                        alignContent: "left",
                                        width: "100%",
                                    }}
                                    data-testid={
                                        creationTestIds.propertyAddressErrorMessage
                                    }
                                >
                                    <IconBox mr={1}>
                                        <Icon
                                            icon="triangle-exclamation"
                                            color="var(--proda-yellow-120)"
                                        />
                                    </IconBox>
                                    A property with this address already exists
                                    in this database.
                                </span>
                            ) : (
                                propertyAddressCheck !== null &&
                                propertyAddressCheck.hasCloseMatch === true &&
                                isAddressFormDirty && (
                                    <span
                                        style={{
                                            fontSize: "0.8rem",
                                            alignContent: "left",
                                            width: "100%",
                                        }}
                                        data-testid={
                                            creationTestIds.propertyAddressErrorMessage
                                        }
                                    >
                                        <IconBox mr={1}>
                                            <Icon
                                                icon="triangle-exclamation"
                                                color="var(--proda-yellow-120)"
                                            />
                                        </IconBox>
                                        A property with a similar address
                                        already exists in this database.
                                    </span>
                                )
                            )}
                        </Box>
                    );
                }}
            </BasePropertyAddressForm>
            <BasePropertyDetailsForm {...detailsProps} />
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-around",
                }}
            >
                <Box
                    sx={{
                        width: "600px",
                    }}
                >
                    <FormSteps
                        nextText="Add Property"
                        currentStep={2}
                        totalSteps={2}
                        editingProperty={true}
                        onBack={onBack}
                        onNext={() =>
                            onSubmit({
                                ...props.property,
                            } as Parameters<typeof onSubmit>[0])
                        }
                        isNextDisabled={!isValid}
                        useSubNavbarIds={true}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default PropertyForm;
